import React, { useState } from 'react';
import { Coffee, Droplets, IceCream, Star, Sparkles, Flame, Wind } from 'lucide-react';

const MenuItem = ({ name, description, price, popular }) => (
  <div className="mb-6 relative bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
    {popular && (
      <div className="absolute -top-3 -right-3 bg-orange-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 shadow-md z-10">
        <Star className="w-3 h-3 fill-current" />
        Popular
      </div>
    )}
    <div className="flex justify-between items-start gap-4 mb-2">
      <h3 className="text-lg font-serif text-orange-900 leading-tight flex-1">{name}</h3>
      <span className="text-lg font-bold text-orange-700 whitespace-nowrap">₹{price}</span>
    </div>
    <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
  </div>
);

const CategorySection = ({ icon: Icon, title, items }) => (
  <div className="mb-10">
    <div className="flex items-center gap-3 mb-5 pb-3 border-b-2 border-orange-300">
      <div className="bg-orange-100 p-2 rounded-lg">
        <Icon className="w-5 h-5 text-orange-700" />
      </div>
      <h2 className="text-2xl font-serif text-orange-900 font-semibold">{title}</h2>
    </div>
    <div className="space-y-4">
      {items.map((item, idx) => (
        <MenuItem key={idx} {...item} />
      ))}
    </div>
  </div>
);

export default function KoffeeMenu() {
  const [activeTab, setActiveTab] = useState('all');

  const menuData = {
    espresso: [
      {
        name: "Classic Espresso",
        description: "Single/Double shot of rich, aromatic espresso",
        price: "90",
        popular: true
      },
      {
        name: "Americano",
        description: "Espresso with hot water, smooth and bold",
        price: "100"
      },
      {
        name: "Cappuccino",
        description: "Espresso with steamed milk and thick foam layer",
        price: "120",
        popular: true
      },
      {
        name: "Cafe Latte",
        description: "Espresso with steamed milk and light foam",
        price: "130"
      },
      {
        name: "Flat White",
        description: "Espresso with velvety microfoam milk",
        price: "140"
      },
      {
        name: "Macchiato",
        description: "Espresso marked with a dollop of foam",
        price: "100"
      },
      {
        name: "Mocha",
        description: "Espresso with chocolate, steamed milk, and whipped cream",
        price: "150"
      }
    ],
    hot: [
      {
        name: "Filter Kaapi",
        description: "Traditional South Indian filter coffee with chicory",
        price: "80",
        popular: true
      },
      {
        name: "Madras Filter Coffee",
        description: "Authentic Chennai-style decoction coffee",
        price: "90"
      },
      {
        name: "Masala Chai",
        description: "Indian spiced tea with cardamom, ginger, and cinnamon",
        price: "70",
        popular: true
      },
      {
        name: "Kesar Badam Milk",
        description: "Saffron and almond infused hot milk",
        price: "110"
      },
      {
        name: "Hot Chocolate",
        description: "Rich Belgian chocolate with steamed milk",
        price: "130"
      },
      {
        name: "Green Tea",
        description: "Premium loose leaf green tea",
        price: "60"
      }
    ],
    cold: [
      {
        name: "Cold Coffee",
        description: "Chilled coffee blended with ice cream and milk",
        price: "150",
        popular: true
      },
      {
        name: "Iced Americano",
        description: "Espresso over ice with cold water",
        price: "120"
      },
      {
        name: "Iced Latte",
        description: "Espresso with cold milk poured over ice",
        price: "140"
      },
      {
        name: "Iced Mocha",
        description: "Chocolate espresso drink served cold",
        price: "160"
      },
      {
        name: "Iced Masala Chai",
        description: "Spiced chai concentrate over ice with milk",
        price: "100"
      },
      {
        name: "Cold Brew",
        description: "Smooth, less acidic cold-steeped coffee",
        price: "140"
      }
    ],
    frappe: [
      {
        name: "Caramel Frappe",
        description: "Blended ice coffee with caramel and whipped cream",
        price: "180",
        popular: true
      },
      {
        name: "Mocha Frappe",
        description: "Chocolate coffee blend with ice and whipped cream",
        price: "180"
      },
      {
        name: "Vanilla Frappe",
        description: "Classic vanilla coffee frappe topped with cream",
        price: "170"
      },
      {
        name: "Hazelnut Frappe",
        description: "Nutty coffee blend with hazelnut syrup",
        price: "190"
      },
      {
        name: "Oreo Frappe",
        description: "Coffee frappe blended with Oreo cookies",
        price: "200",
        popular: true
      },
      {
        name: "Java Chip Frappe",
        description: "Coffee frappe with chocolate chips",
        price: "200"
      }
    ],
    shakes: [
      {
        name: "Chocolate Shake",
        description: "Thick Belgian chocolate shake with ice cream",
        price: "160",
        popular: true
      },
      {
        name: "Strawberry Shake",
        description: "Fresh strawberry blended with vanilla ice cream",
        price: "160"
      },
      {
        name: "Vanilla Shake",
        description: "Classic vanilla ice cream shake",
        price: "150"
      },
      {
        name: "Oreo Shake",
        description: "Oreo cookies blended with vanilla ice cream",
        price: "180",
        popular: true
      },
      {
        name: "KitKat Shake",
        description: "Crunchy KitKat blended with chocolate ice cream",
        price: "190"
      },
      {
        name: "Brownie Shake",
        description: "Chocolate brownie pieces in chocolate shake",
        price: "200"
      },
      {
        name: "Butterscotch Shake",
        description: "Butterscotch ice cream with caramel bits",
        price: "170"
      },
      {
        name: "Mango Shake",
        description: "Alphonso mango pulp blended with ice cream",
        price: "170"
      },
      {
        name: "Nutella Shake",
        description: "Rich Nutella hazelnut chocolate shake",
        price: "210",
        popular: true
      }
    ],
    specialty: [
      {
        name: "Kesar Pista Latte",
        description: "Saffron and pistachio infused latte",
        price: "150"
      },
      {
        name: "Rose Cardamom Coffee",
        description: "Aromatic rose and cardamom spiced coffee",
        price: "140"
      },
      {
        name: "Jaggery Spice Coffee",
        description: "Filter coffee sweetened with organic jaggery",
        price: "110"
      },
      {
        name: "Turmeric Golden Latte",
        description: "Wellness blend with turmeric and black pepper",
        price: "130"
      }
    ]
  };

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'espresso', label: 'Espresso', icon: Coffee },
    { id: 'hot', label: 'Hot Classics', icon: Flame },
    { id: 'cold', label: 'Cold Coffee', icon: Wind },
    { id: 'frappe', label: 'Frappes', icon: Droplets },
    { id: 'shakes', label: 'Shakes', icon: IceCream },
    { id: 'specialty', label: 'Specialty', icon: Sparkles }
  ];

  const filteredData = activeTab === 'all' 
    ? Object.entries(menuData).map(([key, items]) => ({
        key,
        title: categories.find(c => c.id === key)?.label || key,
        icon: categories.find(c => c.id === key)?.icon || Coffee,
        items
      }))
    : [{
        key: activeTab,
        title: categories.find(c => c.id === activeTab)?.label || activeTab,
        icon: categories.find(c => c.id === activeTab)?.icon || Coffee,
        items: menuData[activeTab]
      }];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-700 via-orange-600 to-amber-600 text-white py-10 px-6 text-center shadow-xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.1) 10px, rgba(255,255,255,.1) 20px)'
          }}></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-serif mb-3 tracking-wide font-bold">koffee.com</h1>
          <p className="text-orange-100 text-base md:text-lg font-light tracking-widest mb-1">AUTHENTIC INDIAN COFFEE EXPERIENCE</p>
          <p className="text-orange-200 text-sm">☕ Premium Coffee Since 2020 ☕</p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="sticky top-0 bg-white shadow-lg z-20 border-b border-orange-100">
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 px-4 py-3 min-w-max md:justify-center">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  activeTab === cat.id
                    ? 'bg-orange-600 text-white shadow-lg scale-105'
                    : 'bg-orange-100 text-orange-800 hover:bg-orange-200 hover:scale-105'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Menu Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-16">
        {filteredData.map(section => (
          <CategorySection
            key={section.key}
            icon={section.icon}
            title={section.title}
            items={section.items}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="bg-gradient-to-r from-orange-800 via-orange-700 to-orange-600 text-white py-10 px-6 text-center mt-8 shadow-2xl">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl font-serif mb-4 font-semibold">Visit Us Today</h3>
          <p className="text-base mb-3 text-orange-100 font-medium">Open Daily • 5:00 PM - 01:00 AM</p>
          <p className="text-sm text-orange-200 mb-2">📍 Ulhasnagar-3 </p>
          <p className="text-xs text-orange-300 mb-4">Proudly serving authentic Indian coffee & chai</p>
          <div className="pt-4 border-t border-orange-500">
            <p className="text-xs text-orange-200">© 2024 koffee.com • All Rights Reserved</p>
          </div>
        </div>
      </div>
    </div>
  );
}